/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    SimpleChanges,
    ViewChildren
} from '@angular/core';
import { ResizeEvent } from '../dash-gis-ng2-resizable/interfaces';
import { DeviceService } from '../../../modules/shared/services/device.service';
import { Subject } from 'rxjs';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { Actions, ofActionSuccessful } from '@ngxs/store';
import { RouterNavigation } from '@ngxs/router-plugin';

@Component({
    selector: 'dash-gis-flexible-panel',
    styleUrls: [ './flexible-panel.component.scss' ],
    host: { 'style': 'display: block' },
    templateUrl: './flexible-panel.component.html'
})
export class FlexiblePanelComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
    minimized = false;
    @Input() panelTitle: string;
    @Input() enableResize: boolean;
    @Input() enableScroll: boolean;
    @Input() width: string;
    @Input() disableHistory: boolean;

    @Output() closePanel = new EventEmitter<any>();
    @Output() panelBack = new EventEmitter<any>();
    @Output() resizeEnd = new EventEmitter<ResizeEvent>();

    // for resizable container
    style: Partial<CSSStyleDeclaration> = {};

    // @ViewChild('container', {read: ElementRef}) private container: ElementRef;
    @ViewChildren('container', { read: ElementRef }) private containers: QueryList<ElementRef>;

    private destroySubject = new Subject<void>();

    private _resizeHandleWidth = 6;

    constructor(
        private logger: LoggerService,
        private cdr: ChangeDetectorRef,
        private renderer: Renderer2,
        private deviceService: DeviceService,
        private actions$: Actions
    ) {
        this.actions$.pipe(
            ofActionSuccessful(RouterNavigation)
        ).subscribe(() => this.closePanel.emit());
    }

    ngOnInit() {}

    ngOnDestroy(): void {
        this.destroySubject.next();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.container) {
            if (changes.width) {
                if (changes.width.currentValue) {
                    this.renderer.setStyle(this.container, 'width', changes.width.currentValue);
                } else {
                    this.renderer.removeStyle(this.container, 'width');
                }
            }
        }
    }

    ngAfterViewInit(): void {
        if (this.width && this.container) {
            this.renderer.setStyle(this.container, 'width', this.width);
        }
    }

    onResizeEnd(event: ResizeEvent) {
        this.resizeEnd.emit(event);
        Object.keys(event.edges).forEach(edge => {
            switch (edge) {
                case 'top':
                case 'bottom':
                    this.style.top = `${ event.rectangle.top }px`;
                    break;
                case 'right':
                case 'left':
                    this.style.left = `${ event.rectangle.left }px`;
                    break;
            }
        });
        this.style = {
            ...this.style,
            position: 'absolute',
            height: `${ event.rectangle.height }px`,
            width: `${ event.rectangle.width }px`
        };
    }

    onMinimize() {
        this.minimized = true;
    }

    onRestore() {
        this.minimized = false;
    }

    get container() {
        if (this.containers && this.containers.length > 0) {
            return this.containers.first.nativeElement;
        }
        return null;
    }

    get historyEnabled() {
        return !this.disableHistory && this.panelBack.observers.length > 0;
    }

    get closeEnabled() {
        return this.closePanel.observers.length > 0;
    }

    get isIOS() {
        return this.deviceService.isIOS;
    }

    get resizeHandleWidth() {
        return this._resizeHandleWidth;
    }

    get dragHandleStyle() {
        return {
            position: 'absolute',
            top: `${ this.resizeHandleWidth }px`,
            left: `${ this.resizeHandleWidth }px`,
            right: `${ this.resizeHandleWidth }px`,
            bottom: 0
        };
    }

    private get destroy$() {
        return this.destroySubject.asObservable();
    }
}
